import { getPaymentUIDForAutopay } from "service/paymentServices/getPaymentUIDForAutopay";
import { Dialog, Tab } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import FormInputError from "components/General/FormInputError";
import DialogBackdrop from "./DialogBackdrop";
import config from "config/config";

const AddPaymentMethod = ({
  modalState,
  setModalState,
  selectedPolicy,
  setToastDetail,
  portalReturnURL,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [csrUserState, setCsrUserState] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    reset();
  }, [modalState]);

  const errorOccurred = (title, message) => {
    setToastDetail({
      type: "error",
      title: title,
      message: message,
    });
  };

  const saveAutoPay = async (data) => {
    // First check first and last name matches with log in name
    let primaryOwner = selectedPolicy?.parties?.find(
      (x) => x.person?.taxId === user.identityNumber
    );
    if (csrUser?.userId) {
      // display CSR user error message
      setCsrUserState(true);
      setModalState(false);
    } else {
      if (
        primaryOwner?.person?.firstName?.toLowerCase() ===
          data?.cardholderFirstName?.toLowerCase() ||
        primaryOwner?.person?.lastName?.toLowerCase() ===
          data?.cardholderLastName?.toLowerCase()
      ) {
        setIsLoading(true);
        await getPaymentUIDForAutopay(
          selectedPolicy.contractID,
          `${data.cardholderFirstName} ${data.cardholderLastName}`,
          user.userId,
          selectedPolicy?.blockCode,
          data.zipCode,
          portalReturnURL
        )
          .then((response) => {
            setIsLoading(false);
            setModalState(false);
            if (response?.uid) {
              window.location.href = `${config.HOSTED_PAYMENT_SERVICE_BASE_URL}?uid=${response.uid}`;
              // redirect to HPP
            } else {
              return errorOccurred(
                t("AuthCustomerAddAutoPay-errorTitle"),
                t("AuthCustomerAddAutoPay-errorMessage")
              );
            }
          })
          .catch((error) => {
            console.log(error);
            setModalState(false);
            setIsLoading(false);
            errorOccurred(
              t("AuthCustomerAddAutoPay-errorTitle"),
              t("AuthCustomerAddAutoPay-errorMessage")
            );
          });
      } else {
        setModalState(false);
        return errorOccurred(
          t("AuthCustomerAddAutoPay-errorTitle"),
          t("AuthCustomerAddAutoPay-errorMessage")
        );
      }
    }
  };
  const uuid = uuidv4();
  return (
    <>
      <Dialog
        className="fixed inset-0 top-[20%] z-20 overflow-y-auto"
        as="div"
        open={modalState}
        onClose={() => null}
      >
        <DialogBackdrop />

        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className="ui-default-dialog max-w-xl"
        >
          <Dialog.Title
            as="h2"
            className="font-body text-xl font-bold text-left"
          >
            {t(
              "CustomerManagePayment-manage-payment-card-add-payment-methd-title"
            )}
            <button
              className="ui-default-dialog-close"
              onClick={() => {
                setModalState(false);
              }}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
          </Dialog.Title>
          <hr />
          <Tab.Group>
            <div className="">
              <Tab.List>
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 0 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSelectedTabIndex(0);
                  }}
                >
                  {t(
                    "CustomerManagePayment-manage-payment-card-add-payment-methd-credit-card"
                  )}
                </Tab>
                <Tab
                  className={`text-sm mx-3 hidden pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 1 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSelectedTabIndex(1);
                  }}
                >
                  {t(
                    "CustomerManagePayment-manage-payment-card-add-payment-methd-bank"
                  )}
                </Tab>
              </Tab.List>
            </div>

            <Tab.Panels>
              <Tab.Panel>
                <EverlakeLoadingOverlay isLoading={isLoading}>
                  <div className="mx-3 pt-4">
                    <h3>{t("AuthCustomerAddAutoPay-subTitle")}</h3>
                    <form onSubmit={handleSubmit(saveAutoPay)}>
                      <div className="mt-6">
                        <div className="my-6">
                          <label
                            htmlFor={`fn-cardholderFirstName`}
                            className="block mb-1 font-bold"
                          >
                            {t(
                              "CustomerManagePayment-manage-payment-card-add-payment-methd-first-name"
                            )}
                          </label>
                          <input
                            id={`fn-cardholderFirstName`}
                            type="text"
                            placeholder="Enter First Name"
                            {...register(`cardholderFirstName`, {
                              required: t(
                                "CustomerManagePayment-manage-payment-card-add-payment-methd-first-name-required-error-message"
                              ),
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message: t(
                                  "CustomerManagePayment-manage-payment-card-add-payment-methd-first-name-pattern-error-message"
                                ),
                              },
                            })}
                          />
                          {errors?.cardholderFirstName?.message && (
                            <FormInputError
                              id={`${uuid}-fn-cardholderFirstName-error`}
                              errorMessage={
                                errors?.cardholderFirstName?.message
                              }
                            />
                          )}
                        </div>
                        <div className="my-6">
                          <label
                            htmlFor={`fn-cardholderName`}
                            className="block mb-1 font-bold"
                          >
                            {t(
                              "CustomerManagePayment-manage-payment-card-add-payment-methd-last-name"
                            )}
                          </label>
                          <input
                            id={`fn-cardholderLastName`}
                            type="text"
                            placeholder="Enter Last Name"
                            {...register(`cardholderLastName`, {
                              required: t(
                                "CustomerManagePayment-manage-payment-card-add-payment-methd-last-name-required-error-message"
                              ),
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message: t(
                                  "CustomerManagePayment-manage-payment-card-add-payment-methd-last-name-pattern-error-message"
                                ),
                              },
                            })}
                          />
                          {errors?.cardholderLastName?.message && (
                            <FormInputError
                              id={`${uuid}-fn-cardholderLastName-error`}
                              errorMessage={errors?.cardholderLastName?.message}
                            />
                          )}
                        </div>
                        <div className="my-6">
                          <label
                            htmlFor={`fn-zipCode`}
                            className="block mb-1 font-bold"
                          >
                            {t(
                              "CustomerManagePayment-manage-payment-card-add-payment-methd-zip-code"
                            )}
                          </label>
                          <input
                            id={`fn-zipCode`}
                            type="text"
                            placeholder="Enter Zip Code"
                            {...register(`zipCode`, {
                              required: t(
                                "CustomerManagePayment-manage-payment-card-add-payment-methd-zip-code-required-error-message"
                              ),
                              pattern: {
                                value: /^\d{5}$/,
                                message: t(
                                  "CustomerManagePayment-manage-payment-card-add-payment-methd-zip-code-pattern-error-message"
                                ),
                              },
                              maxLength: {
                                value: 5,
                                message: t(
                                  "CustomerManagePayment-manage-payment-card-add-payment-methd-zip-code-max-length-error-message"
                                ),
                              },
                            })}
                          />
                          <FormInputError
                            id={`${uuid}-fn-zipCode-error`}
                            errorMessage={errors?.zipCode?.message}
                          />
                        </div>
                      </div>
                      <h3>{t("AuthCustomerAddAutoPay-acknowledge")}</h3>
                      <div className="block text-right mt-6">
                        <button
                          type="button"
                          className="ui-btn-secondary min-w-[140px] mt-4"
                          onClick={() => {
                            setModalState(false);
                          }}
                        >
                          {t(
                            "CustomerManagePayment-manage-payment-card-add-payment-methd-cancel"
                          )}
                        </button>
                        <button
                          id="ID_submitPaymentMethod"
                          className="ui-btn-primary min-w-[140px] mt-4"
                        >
                          {t(
                            "CustomerManagePayment-manage-payment-card-add-payment-methd-agree"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </EverlakeLoadingOverlay>
              </Tab.Panel>
              <Tab.Panel className="hidden">
                <div className="mx-3 pt-4">
                  {t(
                    "CustomerManagePayment-manage-payment-card-add-payment-methd-bank"
                  )}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </Dialog.Panel>
      </Dialog>
      <GeneralPurposeDialog
        title={t("CSRErrorMessage-title")}
        desc={t("CSRErrorMessage-message")}
        state={csrUserState}
        setState={setCsrUserState}
        showCloseButton={false}
      />
    </>
  );
};

export default AddPaymentMethod;
